import Button from "components/button/button";
import DatePicker from "components/datepicker/datepicker";
import Input from "components/input/input";
import CreatableSelect from "components/select/creatable";
import Select from "components/select/select";
import TextArea from "components/textarea/textarea";
import React, { useEffect, useState,useContext } from "react";
import { useOccupations } from "redux/modules/occupation/hooks";
import { useTranslation } from "react-i18next";
import { LangContext } from "context/language";
import WYSIWYGEditor from "components/wysiwyg/wysiwyg";
import { useResume } from "redux/modules/resume/hooks";

import {
  // CHILD,
  // CIVIL_STATUS,
  // EYE_COLOR,
  // HAIR_COLOR,
  urlSerializer,
} from "utils/utils";
import { useParams } from "react-router-dom";
import _ from "lodash";
import PopInfo from "components/info-popup/pop-info";
import { randomExtension, parseDateOnly } from "utils/utils";
import { splitHtmlByLimit, getSharedBudget, computeSharedUsage, formatBioCount } from "utils/bio-limit";
import { PROFILE_ABOUT_LIMIT, RESUME_BIO_UNIT } from "constants/options";

/**
 * The shared character count, shown under every pooled text box (MCF-637). All
 * boxes show the same running count — total characters typed across the Bio and
 * the Professional Information boxes, out of the template's shared budget. A box
 * that is itself past the point where the pool ran out also says how much of its
 * own text won't be printed.
 */
function SharedBudgetCounter({ used, budget, overflow = 0 }) {
  const over = used > budget;
  const tone = over ? "text-bioCounterOver" : "text-gray-700";
  return (
    <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
      <span className="flex items-baseline gap-x-2 whitespace-nowrap">
        <span className={`text-sm font-medium ${tone}`}>
          {formatBioCount(used)} / {formatBioCount(budget)}
        </span>
        <span className="text-xs text-gray-500">
          {over
            ? `${formatBioCount(used - budget)} over the shared limit`
            : "shared across all boxes"}
        </span>
      </span>
      {overflow > 0 && (
        <span className="text-xs font-semibold text-bioCounterOver whitespace-nowrap">
          {formatBioCount(overflow)} here won&rsquo;t be printed
        </span>
      )}
    </div>
  );
}

export default function MoreInfoPanel(props) {
  const {EYE_COLOR,HAIR_COLOR, CHILD,
    CIVIL_STATUS} = useContext(LangContext)
  const {t} = useTranslation();
  const { http } = global.services;
  const { details, setState, setInitialMount, onNext, onBack,state, userData } = props;

  // The ceiling is the template's, never a constant. Templates differ.
  // Shading is painted by a mirror element (see components/wysiwyg), so Quill's
  // document is never modified and the stored bio cannot drift from the editor.
  const BIO_OVERFLOW_SHADING = true;

  // Bio is the first field in the shared pool, so it may use the whole template
  // budget; the Professional fields take what is left (MCF-637).
  const bioLimit = getSharedBudget(state?.templateName);
  const { profileId } = useParams();
  const [fields, setFields] = useState({
    personal: {},
    professional: {},
  });
  const [loadBio,setLoadBio] = useState(true)
  const [bio, setBio] = useState('');
  const {resumes, setBioResume} = useResume()

  const {
    refresh: refreshOccupation,
    initialize: initializeOccupation,
    occupations,
    setOccupations,
  } = useOccupations();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Build on the LATEST state.more (prev.more), not the `details` prop, which
    // is a render-time snapshot. Using the stale snapshot could drop a field the
    // user just set (e.g. a Date of Birth) when another change lands right after.
    setState((prev) => ({
      ...prev,
      more: {
        ...(prev.more || {}),
        [name]: value,
      },
    }));

    setInitialMount(false);
  };

  const handleChangeOccupation = (e) => {
    const { name, value } = e.target;
    const mappedValues = (value || []).map((obj) => {
      return obj.label;
    });
    setState((prev) => ({
      ...prev,
      more: {
        ...(prev.more || {}),
        [name]: mappedValues,
      },
    }));

    setInitialMount(false);
  };

  const getOccupations = async () => {
    try {
      const url = urlSerializer("public/occupations", {
        user_profile_id: profileId,
      });
      const { data } = await http.get(url);
      const options = data.map((opt) => ({
        label: opt.name,
        value: opt.id,
      }));
      setOccupations(options);
    } catch (err) {
      console.error("getOccupations", err);
    }
  };
  useEffect(()=>{
    if(details){
        let moreInfo = resumes.find(item => item.id === state.id)?.more_info || details || '';
        if (!_.isEmpty(moreInfo.mention_bio)) {// this is not available when it was first created
            let description = moreInfo.bio;
            Object.keys(moreInfo.mention_bio).forEach((k) => {
              const pUsername = moreInfo.mention_bio[k]?.p_username;
              // Regex to match href containing username and capture the name between <a> and </a>
              const regex = new RegExp(`(<a[^>]*key="[^"]*${pUsername}[^"]*"[^>]*>)(.*?)(<\/a>)`, 'g');
              
              description = description.replace(regex, (match, openingTag, name, closingTag) => {
                return `${openingTag}@${pUsername}${closingTag}`;
              });
            });
            setBio(description)
          }else if(!_.isEmpty(moreInfo.mentions)){// for the new created fetch the mention here
            let description = moreInfo.bio;
            moreInfo.mentions.forEach((pUsername) => {
              // Regex to match href containing username and capture the name between <a> and </a>
              const regex = new RegExp(`(<a[^>]*key="[^"]*${pUsername}[^"]*"[^>]*>)(.*?)(<\/a>)`, 'g');
              
              description = description.replace(regex, (match, openingTag, name, closingTag) => {
                return `${openingTag}@${pUsername}${closingTag}`;
              });
            });
            setBio(description)
          }else{
            setBio(moreInfo?.bio)

          }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[state.id,resumes])

  useEffect(() => {
    if (details) {
      setFields({
        personal: {
          birthdate: {
            label: t('dateBirth'),
            type: "datepicker",
            value: details?.birthdate ? parseDateOnly(details?.birthdate) : null,
            options: [],
          },
          birth_location: {
            label: t('birthLocation'),
            type: "text",
            value: details.birth_location,
            options: [],
          },
          // civil_status: {
          //   label: t('civilStatus'),
          //   type: "select",
          //   value: details.civil_status,
          //   options: CIVIL_STATUS,
          // },
          // children: {
          //    label: t('children'),
          //   type: "select",
          //   value: details.children,
          //   options: CHILD,
          // },
          other_name: {
            label: t('otherNames'),
            type: "text",
            value: details.other_name,
            options: [],
          },
          hair_color: {
            label: t('hairColour'),
            type: "select",
            value: details.hair_color,
            options: HAIR_COLOR,
          },
          eye_color: {
            label: t('eyeColour'),
            type: "select",
            value: details.eye_color,
            options: EYE_COLOR,
          },
          height: {
            label: t('heightCm'),
            type: "text",
            value: details.height,
            options: [],
          },
          phone_number: {
            label: t("phone"),
            type: "text",
            value: details.phone_number,
            options: [],
          },
          email: {
            label: t("emailAddress"),
            type: "text",
            value: details.email,
            options: [],
          },
        },
        professional: {
          skills: {
            label: t('abilitiesSkills'),
            type: "textarea",
            value: details.skills || "",
            options: [],
          },
          education: {
            label: t("education"),
            type: "textarea",
            value: details.education || "",
            options: [],
          },
          training: {
            label: t("training"),
            type: "textarea",
            value: details.training || "",
            options: [],
          },
          experience: {
            label: t("experience"),
            type: "textarea",
            value: details.experience || "",
            options: [],
          },
          awards: {
            label: t("awards"),
            type: "textarea",
            value: details.awards || "",
            options: [],
          },
          representation: {
            label: t("representation"),
            type: "textarea",
            value: details.representation || "",
            options: [],
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  useEffect(() => {
    if (initializeOccupation) getOccupations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshOccupation, initializeOccupation]);

  const handleBioChange = React.useCallback((val) => {
      setBio(prev => {
        if (bio === val) return prev;
        return val;
      });
      let mention = updateMentionsToName(val);
      setBioResume(mention)
      
    }, []);

    function updateMentionsToName(html) {
        if (!html) return html;
        if(details){
          let moreInfo = resumes.find(item => item.id === state.id)?.more_info || details || '';
          if (!_.isEmpty(moreInfo.mention_bio)) {// this is not available when it was first created
              // Matches: <a ...>@username</a>  -> captures opening tag, username, closing tag
            let mentionBio = moreInfo.mention_bio;
            const regex = /(<a[^>]*>)@([a-zA-Z0-9_.-]+)(<\/a>)/g;

            return html.replace(regex, (match, openingTag, username, closingTag) => {
              const data = mentionBio[username] || mentionBio[username.toLowerCase()];
              if (!data?.p_name) return match; // leave untouched if no mapping found
              return `${openingTag}${data.p_name}${closingTag}`;
            });
          }
       }
       return html;
    }

  /** Drop everything past the limit, leaving the in-limit text as the bio. */
  const handleTrimToFit = () => {
    const { keptHtml } = splitHtmlByLimit(bio, bioLimit, RESUME_BIO_UNIT);
    setBio(keptHtml);
    setBioResume(updateMentionsToName(keptHtml));
    setState(prev => ({ ...prev, more: { ...(prev.more || {}), bio: keptHtml } }));
  };

  const onBlurBioChange = () => {
    setState(prev => {
      return { ...prev, more: {
        ...(prev.more || {}),
        bio,
      }, };
    });
  };

  // Live shared-budget usage across bio + the six Professional fields. `details`
  // seeds, the live edit buffer (state.more) overrides, and the local `bio`
  // state is the freshest bio. Drives the one shared counter under every box.
  const sharedUsage = React.useMemo(
    () => computeSharedUsage(
      { ...(details || {}), ...(state?.more || {}), bio },
      state?.templateName
    ),
    [details, state?.more, state?.templateName, bio]
  );
  const sharedUsedTotal = sharedUsage.totalUsed;
  const sharedBudgetTotal = sharedUsage.budget;

  return (
    <div className="w-full h-full border-r border-resumeBorder">
      <div
        className="w-full overflow-y-auto overflow-x-hidden custom-scroll"
        style={{ height: "calc(100vh - 174px)" }}>
        <div className="w-full px-2 2sm:px-15.5 py-10.6">
          <div className="w-full flex flex-row gap-x-2 items-center">
            <h1 className="text-lightBlack text-xl font-semibold">{t('moreInfo')}</h1>
            <PopInfo containerClass="relative" type="resume_more_info" />
          </div>
          <div className="w-full mt-4">
            <div className="flex items-center justify-between gap-x-3 mb-2">
              <span className="text-sm font-semibold text-darkerGray">Bio</span>
              <span className="text-xs text-gray-500">
                from your Milestones About section
              </span>
            </div>
            <WYSIWYGEditor
                isResume = {true}
                label=""
                optional={false}
                value={bio}
                limitChar={bioLimit}
                limitUnit={RESUME_BIO_UNIT}
                overflowShading={BIO_OVERFLOW_SHADING}
                hideCounter={true}
                onChange={handleBioChange}
                preserveWhitespace={true}
                minimalToolBar={true}
                onBlur = {onBlurBioChange}
              />
            <SharedBudgetCounter
              used={sharedUsedTotal}
              budget={sharedBudgetTotal}
              overflow={sharedUsage.perField?.bio?.overflow || 0}
            />

            {BIO_OVERFLOW_SHADING && (
            <div className="w-full mt-4 rounded-md border border-gray shadow-md p-4 flex gap-x-4">
              <div className="w-8 h-8 flex-none rounded-md bg-blue-50 text-primary flex items-center justify-center text-base font-bold">
                i
              </div>
              <div className="flex-1 flex flex-col gap-y-2">
                <div className="text-sm font-semibold text-lightBlack">
                  This bio came from your Milestones profile
                </div>
                <p className="text-xs leading-5 text-darkerGray">
                  We copied it from the <strong>About</strong> section of your profile,
                  where you can write up to{' '}
                  <strong>{formatBioCount(PROFILE_ABOUT_LIMIT)} characters</strong>. This
                  resume template shares{' '}
                  <strong>{formatBioCount(bioLimit)} characters</strong> across the Bio and
                  all of the Professional Information boxes below &mdash; the counter under
                  each box shows how many are left.
                </p>
                <p className="text-xs leading-5 text-darkerGray">
                  Anything{' '}
                  <span className="text-bioOverflowText bg-bioOverflowBg">greyed out</span>{' '}
                  is past that limit. It will <strong>not</strong> appear on this resume or
                  in the PDF you download. Nothing is deleted &mdash; your full About
                  section stays exactly as it is.
                </p>
                <p className="text-xs leading-5 text-darkerGray">
                  Edit the text here to choose what makes the cut.
                </p>
                <div className="flex flex-wrap gap-x-6 gap-y-2 mt-1">
                  <button
                    type="button"
                    onClick={handleTrimToFit}
                    className="text-xs font-semibold text-primary"
                  >
                    Trim to fit automatically
                  </button>
                  <a
                    href={`/${userData?.user?.username}-${randomExtension()}/profile/${userData?.profile?.id}?aboutSection=true`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-semibold text-primary"
                  >
                    Open my About section
                  </a>
                </div>
              </div>
            </div>
            )}
            
          </div>
          <div className="w-full grid grid-cols-1 2sm:grid-cols-2 gap-7 mt-8">
            {Object.keys(fields.personal).map((key) => (
              <FormBuilder
                key={key}
                label={fields.personal[key].label}
                type={fields.personal[key].type}
                name={key}
                defaultValue={fields.personal[key].value}
                placeholder={fields.personal[key].label}
                options={fields.personal[key].options}
                onChange={handleInputChange}
              />
            ))}
          </div>
          <div className="w-full mt-9">
            <h1 className="text-lightBlack text-xl font-semibold">
              {t('professionalInformation')}
            </h1>
            <div className="w-full mt-6">
              {Object.keys(fields.professional).map((key) => {
                const pooled = sharedUsage.perField?.[key];
                return (
                  <div key={key} className="w-full">
                    <FormBuilder
                      label={fields.professional[key].label}
                      type={fields.professional[key].type}
                      name={key}
                      defaultValue={fields.professional[key].value}
                      placeholder={fields.professional[key].label}
                      options={fields.professional[key].options}
                      onChange={handleInputChange}
                      changeOccupation={handleChangeOccupation}
                    />
                    {pooled && (
                      <SharedBudgetCounter
                        used={sharedUsedTotal}
                        budget={sharedBudgetTotal}
                        overflow={pooled.overflow || 0}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {/* footer */}
      <div className="w-full py-4 px-6 border-t border-resumeBorder">
        <div className="w-full flex justify-center 2sm:justify-end space-x-5">
          <Button
            buttonName={t('back')}
            buttonTextClass="text-sm font-semibold text-lightBlack"
            buttonRadius="6px"
            buttonClass="bg-thBg px-12 py-3 2sm:px-17.5 2sm:py-5"
            onClick={onBack}
          />
          <Button
            buttonName={t('next')}
            buttonTextClass="text-sm font-semibold text-white"
            buttonRadius="6px"
            buttonClass="bg-primary px-12 py-3 2sm:px-17.5 2sm:py-5"
            onClick={onNext}
          />
        </div>
      </div>
    </div>
  );
}

function FormBuilder({
  type,
  name = "",
  label = "",
  defaultValue = "",
  placeholder = "",
  onChange = () => {},
  changeOccupation = () => {},
  options = [],
}) {
  if (type === "text") {
    return (
      <Input
        outsideLabel={label}
        inputContainerClass="w-full"
        defaultValue={defaultValue}
        name={name}
        placeholder={placeholder}
        outsideLabelClass="text-sm font-bold text-darkerGray"
        noLabelPaddingY="1rem"
        type="text"
        onChange={onChange}
      />
    );
  } else if (type === "select") {
    let selectValue = null;
    if (defaultValue) {
      selectValue = { label: defaultValue, value: defaultValue };
    }
    return (
      <Select
        label={label}
        formContainer="w-full"
        options={options}
        placeholder={placeholder}
        name={name}
        defaultValue={selectValue}
        height={58}
        onChange={(e) => onChange({ target: { name: name, value: e.value } })}
      />
    );
  } else if (type === "datepicker") {
    return (
      <DatePicker
        label={label}
        formContainer="w-full"
        icon="default"
        selected={defaultValue ? new Date(defaultValue) : null}
        onChange={(date) =>
          // Store the picked date at LOCAL NOON so it survives UTC conversion
          // without shifting a day (birthdate off-by-one).
          onChange({ target: { name: name, value: date ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0) : null} })
        }
        placeholderText={placeholder}
      />
    );
  } else if (type === "textarea") {
    return (
      <TextArea
        formContainer="w-full"
        label={label}
        defaultValue={defaultValue}
        name={name}
        inputClass="p-4.5 text-base leading-6 text-lightBlack"
        rows={4}
        type="text"
        onChange={onChange}
      />
    );
  } else if (type === "creatable") {
    // ======= default value =============
    let selectDefaultValue = [];
    if (name === "occupations") {
      selectDefaultValue = (defaultValue || []).map((value) => {
        let obj = _.find(options, { label: value });
        return { label: value, value: obj?.value };
      });
    }
    return (
      <CreatableSelect
        isMulti
        label={label}
        options={options}
        placeholder={placeholder}
        name={name}
        value={selectDefaultValue || null}
        height={58}
        onChange={(e) => changeOccupation({ target: { name: name, value: e } })}
      />
    );
  }
}
